import { buttonStyle } from 'components/buttons/buttonStyle';
import { DataTable } from 'components/tables/data-table';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { Button } from 'components/ui/button';
import { Skeleton } from 'components/ui/skeleton';
import { ChangeEvent, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
    useGetInvoiceLogoQuery,
    useResetInvoiceLogoMutation,
    useUpdateInvoiceLogoMutation,
} from 'store/api/tenants.ts';
import { TenantConfigurationDto } from 'types/tenants.ts';
import { t } from 'utils/getTranslations.ts';
import { useHasPermissions } from 'utils/permissionUtil';

const MAX_LOGO_DIMENSION = 3000;

interface Props {
    tenantConfiguration: TenantConfigurationDto;
}

export function readImageDimensions(file: File): Promise<{ width: number; height: number } | null> {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            const { width, height } = img;
            URL.revokeObjectURL(img.src);
            resolve({ width, height });
        };
        img.onerror = () => {
            URL.revokeObjectURL(img.src);
            resolve(null);
        };
        img.src = URL.createObjectURL(file);
    });
}

export function CompanyLogoOnInvoiceControl({ tenantConfiguration }: Props): JSX.Element | null {
    const invoiceLogo = tenantConfiguration.invoiceLogo;
    const hasLogoTemplates = invoiceLogo?.hasLogoTemplates ?? false;
    const { data: logoUrl, isLoading: isLogoLoading } = useGetInvoiceLogoQuery(undefined, { skip: !hasLogoTemplates });
    const [updateInvoiceLogo, { isLoading: isUploading }] = useUpdateInvoiceLogoMutation();
    const [resetInvoiceLogo, { isLoading: isResetting }] = useResetInvoiceLogoMutation();
    const hasUpdatePermission = useHasPermissions(['UPDATE.BACK_OFFICE_SETTINGS']);
    const [validationError, setValidationError] = useState<string | null>(null);

    const hasCustomLogo = invoiceLogo?.hasCustomLogo ?? false;
    const recommendedWidth = invoiceLogo?.recommendedWidth ?? 0;
    const recommendedHeight = invoiceLogo?.recommendedHeight ?? 0;
    const isChangeDisabled = !hasUpdatePermission || isUploading || isResetting;

    const columns = useMemo<DataTableColumnDef<TenantConfigurationDto>[]>(() => {
        const dimensionValues = {
            width: recommendedWidth.toString(),
            height: recommendedHeight.toString(),
            maxDimension: MAX_LOGO_DIMENSION.toString(),
        };

        const validateLogoFile = async (file: File): Promise<string | null> => {
            if (file.type !== 'image/png') {
                return t('configuration.invoice-logo-invalid-type');
            }
            const dimensions = await readImageDimensions(file);
            if (!dimensions) {
                return t('configuration.invoice-logo-invalid-type');
            }
            if (dimensions.width > MAX_LOGO_DIMENSION || dimensions.height > MAX_LOGO_DIMENSION) {
                return t('configuration.invoice-logo-invalid-dimensions', dimensionValues);
            }
            return null;
        };

        const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (!file) {
                return;
            }
            const error = await validateLogoFile(file);
            setValidationError(error);
            if (!error) {
                await updateInvoiceLogo(file);
            }
        };

        return [
            {
                header: t('configuration.invoice-logo'),
                id: 'invoice-logo',
                enableSorting: false,
                meta: {
                    widthWeight: 2,
                    // The logo preview is a fixed 237px image and the two action buttons sit on one
                    // nowrap line beneath it, so the column can never be narrower than that stack.
                    minPx: 300,
                },
                cell: () => (
                    <div className="flex flex-col gap-2 py-2">
                        {logoUrl ? (
                            <img
                                src={logoUrl}
                                alt={t('configuration.invoice-logo')}
                                className="h-[60px] w-[237px] object-contain object-left"
                            />
                        ) : isLogoLoading ? (
                            // Reserve the image slot while the logo blob loads, so content below
                            // (incl. hash-scroll targets) doesn't shift when it pops in.
                            <Skeleton className="h-[60px] w-[237px]" />
                        ) : null}
                        <span className="text-caption text-fg-secondary">
                            {t('configuration.invoice-logo-requirements', dimensionValues)}
                        </span>
                        {validationError ? <span className="text-caption text-error">{validationError}</span> : null}
                        <div className="flex flex-row gap-2">
                            {/* A label, so a click on it opens the file input it holds. */}
                            <Button
                                {...buttonStyle(
                                    { variant: 'outlined' },
                                    'data-disabled:pointer-events-none data-disabled:opacity-50'
                                )}
                                nativeButton={false}
                                render={<label />}
                                disabled={isChangeDisabled}
                            >
                                <FormattedMessage id="configuration.invoice-logo-change" />
                                <input
                                    hidden
                                    type="file"
                                    accept=".png"
                                    disabled={isChangeDisabled}
                                    onChange={(event) => void handleFileChange(event)}
                                />
                            </Button>
                            <Button
                                {...buttonStyle({ variant: 'outlined' })}
                                disabled={!hasUpdatePermission || !hasCustomLogo || isUploading || isResetting}
                                onClick={() => {
                                    setValidationError(null);
                                    void resetInvoiceLogo();
                                }}
                            >
                                <FormattedMessage id="configuration.invoice-logo-reset" />
                            </Button>
                        </div>
                    </div>
                ),
            },
            {
                header: '',
                id: 'spacer',
                enableSorting: false,
                meta: {
                    widthWeight: 3,
                },
                cell: () => null,
            },
        ];
    }, [
        recommendedWidth,
        recommendedHeight,
        logoUrl,
        isLogoLoading,
        validationError,
        hasUpdatePermission,
        isChangeDisabled,
        hasCustomLogo,
        isUploading,
        isResetting,
        updateInvoiceLogo,
        resetInvoiceLogo,
    ]);

    const rows = useMemo(() => [tenantConfiguration], [tenantConfiguration]);

    if (!hasLogoTemplates) {
        return null;
    }

    return (
        <>
            <div className="flex flex-row justify-between">
                <h3 className="text-h3 text-balance">
                    <FormattedMessage id="configuration.invoice-logo-control" />
                </h3>
            </div>
            <DataTable<TenantConfigurationDto>
                columns={columns}
                data={rows}
                columnConfigId="configuration-invoice-logo"
            />
        </>
    );
}
